import { defineDocument } from '../../schema';
export const gameFinishEachOthersSandwichesDocument = defineDocument({
    id: 'game:finish-each-others-sandwiches',
    kind: 'game',
    slug: 'finish-each-others-sandwiches',
    route: '/discover/games/finish-each-others-sandwiches',
    seo: {
        title: "Discover Finish Each Other's Sandwiches",
        description: "A cooperative party game testing how in-sync you are. Match answers across 4 rounds to score big. Can you finish each other's sandwiches?",
        imageAsset: 'coverFinishEachOthersSandwiches',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: "Finish Each Other's Sandwiches",
            },
        ],
        description: [
            {
                type: 'text',
                text: 'A sandwich-themed collaborative game where players prove they are psychically in sync by matching answers.',
            },
        ],
        imageAsset: 'coverFinishEachOthersSandwiches',
        imageAlt: "Finish Each Other's Sandwiches game cover",
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Seeking all psychics! Prove you and your crew are in sync.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Work together to climb the scoreboard and impress the Sandwich Spirits by matching answers across four rounds: Quick Bites, Snack Attack, Same Order, and The Whole Enchilada. Each round has its own twist, but the goal stays the same: the more your minds align, the more points you earn.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Every now and then, you'll swear someone reached into your brain and finished your… sandwich.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '2-16 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Cooperative',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free Input, Selection, Deduction',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~17 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'October 26, 2025',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '6701bc6c35797c2928318643',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players work together across four rounds: Quick Bites, Snack Attack, Same Order, and The Whole Enchilada. Score updates are given after each round.',
                        },
                    ],
                },
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Quick Bites: Players are presented with a series of five opinion-based questions and select an answer from multiple choices.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Snack Attack: Players are presented with a series of five words and input a single word they associate to each of them.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Same Order: Players are presented with a series of five self-reflection statements, multiple choices to finish the statement, and a designated player. Players try to anticipate what the designated player will pick.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The Whole Enchilada: Players are presented an open-ended prompt and finish the sentence.',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Points are awarded based on how many players match answers relative to the number of players.',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Quick Bites: 500 points max per question, 2500 for the round.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Snack Attack: 500 points max per word, 2500 for the round.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Same Order: 500 points max per statement, 2500 for the round.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The Whole Enchilada: 1000 points max per prompt, 4000 for the round.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'This is a cooperative game. Aim for the highest score possible! Achievement levels:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Not Impressive: Less than 2000 points',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Palatably Predicted: Between 2000 and 2499 points',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Tastefully Telepathed: Between 2500 and 2999 points',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Deliciously Divined: Between 3000 and 3499 points',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Mouth-Wateringly Manifested: Greater than 3500 points',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Word associations in Snack Attack',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Prompt completions in Whole Enchilada',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Questions and answers in Quick Bites',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Words in Snack Attack',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Self-reflection statements and answers in Same Order',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Prompts in Whole Enchilada',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: medium',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: teams testing their sync, friend groups, co-op enthusiasts',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/fresh-out-of-the-oven-finish-each-others-sandwiches',
                            children: [
                                {
                                    type: 'text',
                                    text: "Finish Each Other's Sandwiches blog post",
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '6701bc6c35797c2928318643',
                },
            ],
        },
    ],
});
//# sourceMappingURL=finish-each-others-sandwiches.js.map